import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { StatusBadge, type BadgeTone } from '../ui/StatusBadge';
import { apiFetch } from '../../lib/api';
import { formatNumber, formatPercent } from '../../lib/format';
import { ApiError, type DeliverabilityScore, type DeliverabilityStatus } from '../../types/api';
import { addDays, dayCount, isValidKey, rangeToPeriod, todayKey } from './dateRange';

/** The API refuses a window longer than this (it reads every message queued in it). */
export const DELIVERABILITY_MAX_DAYS = 92;
/** Under this many sent mails the API says "insufficient" instead of grading. */
export const DELIVERABILITY_MIN_SENT = 200;

const DAY_MS = 24 * 3600 * 1000;

const STATUS_VIEW: Record<DeliverabilityStatus, { tone: BadgeTone; icon: string; label: string }> =
  {
    green: { tone: 'success', icon: '●', label: 'Good' },
    amber: { tone: 'warning', icon: '▲', label: 'Watch' },
    red: { tone: 'danger', icon: '■', label: 'Too high' },
    insufficient: { tone: 'neutral', icon: '○', label: 'Not enough mail' },
  };

/**
 * Same limits as `packages/core/src/send/deliverability.ts` (the API grades; these
 * are only the numbers quoted in the explanation).
 */
const LIMITS = {
  bounce: { amber: '2 %', red: '5 %' },
  complaint: { amber: '0.1 %', red: '0.3 %' },
  unsubscribe: { amber: '0.5 %', red: '1 %' },
};

const RATES = [
  {
    key: 'bounce',
    label: 'Bounce rate',
    rate: 'bounceRate',
    help: 'Mails that could not be delivered (wrong or dead addresses).',
  },
  {
    key: 'complaint',
    label: 'Complaint rate',
    rate: 'complaintRate',
    help: 'People who pressed "report spam". The most serious signal for mailbox providers.',
  },
  {
    key: 'unsubscribe',
    label: 'Unsubscribe rate',
    rate: 'unsubscribeRate',
    help: 'People who unsubscribed in this period. A hint about content or audience fit, not a sending block.',
  },
] as const;

/** The range as the API wants it. A 92-day window across a DST change is 1 h too long, so it is trimmed. */
function windowFor(from: string, to: string): { from: number; to: number } {
  const p = rangeToPeriod({ start: from, end: to });
  return { from: p.from, to: Math.min(p.to, p.from + DELIVERABILITY_MAX_DAYS * DAY_MS) };
}

/** Why a typed range cannot be asked for; null = fine. */
export function rangeProblem(from: string, to: string): string | null {
  if (!isValidKey(from) || !isValidKey(to)) return 'Pick a start and an end date.';
  if (from > to) return 'The start date must be on or before the end date.';
  if (dayCount({ start: from, end: to }) > DELIVERABILITY_MAX_DAYS) {
    return `Pick at most ${DELIVERABILITY_MAX_DAYS} days.`;
  }
  if (from > todayKey()) return 'The start date cannot be in the future.';
  return null;
}

const PRESETS = [
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 92, label: 'Last 92 days' },
];

/**
 * K6 deliverability score on the statistics page: bounce, complaint and unsubscribe
 * rate over a chosen window (default 30 days, at most 92), each with a status that
 * is written out, not only coloured.
 */
export function DeliverabilityCard() {
  const [today] = useState(() => todayKey());
  const [from, setFrom] = useState(() => addDays(today, -29));
  const [to, setTo] = useState(today);
  const problem = rangeProblem(from, to);
  const win = problem === null ? windowFor(from, to) : null;

  const query = useQuery({
    queryKey: ['stats', 'deliverability', win?.from, win?.to],
    queryFn: () =>
      apiFetch<DeliverabilityScore>(`/stats/deliverability?from=${win!.from}&to=${win!.to}`),
    enabled: win !== null,
    retry: false,
  });

  const apiError =
    query.error instanceof ApiError && query.error.body.error === 'invalid_range'
      ? `The API refused this period. Pick at most ${DELIVERABILITY_MAX_DAYS} days, starting before they end.`
      : query.isError
        ? 'The deliverability score could not be loaded. Try again in a moment.'
        : null;
  const data = query.data;

  return (
    <section aria-labelledby="deliv-title">
      <Card className="p-4 flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <h2 id="deliv-title" className="m-0 text-base font-semibold">
              Deliverability
            </h2>
            <p className="m-0 text-sm text-muted">
              How healthy your sending is. Mailbox providers such as Gmail and Yahoo judge you on
              these three numbers.
            </p>
          </div>
          {data && !problem ? (
            <StatusBadge
              tone={STATUS_VIEW[data.overall].tone}
              icon={STATUS_VIEW[data.overall].icon}
            >
              Overall: {STATUS_VIEW[data.overall].label}
            </StatusBadge>
          ) : null}
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="deliv-from" className="text-xs text-muted">
              From
            </label>
            <input
              id="deliv-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              aria-invalid={problem !== null || undefined}
              aria-describedby="deliv-range-help"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="deliv-to" className="text-xs text-muted">
              To (included)
            </label>
            <input
              id="deliv-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              aria-invalid={problem !== null || undefined}
              aria-describedby="deliv-range-help"
            />
          </div>
          <div role="group" aria-label="Quick periods" className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.days}
                type="button"
                className="rounded-[7px] border border-border bg-card px-2.5 py-1.5 text-sm"
                onClick={() => {
                  setTo(today);
                  setFrom(addDays(today, -(p.days - 1)));
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <p id="deliv-range-help" className="m-0 text-xs text-muted">
          Up to {DELIVERABILITY_MAX_DAYS} days. Counts mails queued in the period, flow mails
          included.
        </p>

        {problem ? (
          <p className="m-0 text-sm text-danger-text" role="alert">
            {problem}
          </p>
        ) : apiError ? (
          <Alert>{apiError}</Alert>
        ) : query.isPending ? (
          <p className="m-0 text-muted">Loading the deliverability score…</p>
        ) : data ? (
          <>
            {data.overall === 'insufficient' ? (
              <p className="m-0 text-sm" role="status">
                Only {formatNumber(data.sent)} {data.sent === 1 ? 'mail was' : 'mails were'} sent in
                this period. Under {DELIVERABILITY_MIN_SENT} the rates say too little (one complaint
                in 50 mails would look like 2 %), so no colour is given. Pick a longer period.
              </p>
            ) : (
              <p className="m-0 text-sm text-muted">
                {formatNumber(data.sent)} mails sent in this period.
              </p>
            )}
            <ul
              aria-label="Deliverability rates"
              className="m-0 grid list-none gap-3 p-0 sm:grid-cols-3"
            >
              {RATES.map((r) => {
                const status = data[r.key];
                const view = STATUS_VIEW[status];
                return (
                  <li
                    key={r.key}
                    data-status={status}
                    className="flex flex-col gap-1 rounded-[10px] border border-border p-3"
                  >
                    <span className="text-xs text-muted">{r.label}</span>
                    <span className="text-xl font-semibold tabular-nums">
                      {formatPercent(data[r.rate])}
                    </span>
                    <span>
                      <StatusBadge tone={view.tone} icon={view.icon}>
                        {view.label}
                      </StatusBadge>
                    </span>
                    <span className="text-xs text-muted">{r.help}</span>
                  </li>
                );
              })}
            </ul>
          </>
        ) : null}

        <details className="text-sm">
          <summary className="cursor-pointer">How the colours are decided</summary>
          <ul className="mt-2 mb-0 pl-5 text-muted">
            <li>
              Complaints: Good under {LIMITS.complaint.amber}, Watch from {LIMITS.complaint.amber},
              Too high from {LIMITS.complaint.red}. Google and Yahoo ask bulk senders to stay under
              0.1 % and never reach 0.3 %.
            </li>
            <li>
              Bounces: Good under {LIMITS.bounce.amber}, Watch from {LIMITS.bounce.amber}, Too high
              from {LIMITS.bounce.red}. Amazon SES reviews an account at 5 %, and our own sending
              pauses a campaign at 2 %.
            </li>
            <li>
              Unsubscribes: Good under {LIMITS.unsubscribe.amber}, Watch from{' '}
              {LIMITS.unsubscribe.amber}, Too high from {LIMITS.unsubscribe.red}. A rule of thumb,
              not a provider limit.
            </li>
            <li>The overall status is the worst of the three.</li>
          </ul>
        </details>
      </Card>
    </section>
  );
}
